/**
 * Aspect overlay controller — the "colour by aspect", legend, and
 * "aspect web" features extracted out of `App`.
 *
 * The user's PICKS (colour key, legend hover/pin, web keys) live in the
 * WorkspaceStore's `aspect` slice, shared with every workbench primitive;
 * this hook layers on what the store deliberately doesn't know:
 *
 *   - **derivations off the model** (key list, legend rows, member
 *     highlight, the synthetic overlay nodes/edges) — the store is
 *     model-free.
 *   - **localStorage prefs** — colour/web picks survive a reload. Global
 *     (not per-workspace) on purpose: keys are semantic names like `team`
 *     that recur across workspaces. Only EXPLICIT picks write storage; the
 *     stale-key drop below clears store state without erasing the pref, so
 *     visiting a workspace that lacks the key doesn't lose it for the
 *     workspaces that have it. Seed the store with `readAspectPrefs()`.
 *
 * Legend-clearing on colour-key change is the store's own invariant
 * (`setAspectColorKey` clears hover/pin atomically) — no effect needed.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import {
  aspectKeysOf,
  aspectLegend,
  aspectMemberIds,
  aspectMemberInterfaces,
  type LegendEntry,
} from "../aspectColors";
import { buildAspectOverlay, type AspectOverlay } from "../aspect-overlay";
import {
  useWorkspaceAspect,
  type WorkspaceStore,
} from "../workbench/WorkspaceStore.js";

export interface AspectHighlight {
  readonly modules: readonly string[];
  readonly interfaces: readonly string[];
}

export interface AspectOverlayController {
  /** Sorted distinct string-valued label keys present in the model. */
  readonly aspectKeys: readonly string[];
  /** Active "colour by" key, or null when off. */
  readonly aspectColorKey: string | null;
  readonly setAspectColorKey: Dispatch<SetStateAction<string | null>>;
  /** Legend rows (value + colour) for the active colour key. */
  readonly aspectLegendEntries: readonly LegendEntry[];
  readonly legendHoverValue: string | null;
  readonly setLegendHoverValue: Dispatch<SetStateAction<string | null>>;
  readonly legendPinnedValue: string | null;
  readonly setLegendPinnedValue: Dispatch<SetStateAction<string | null>>;
  /** Effective highlight (hover over pin), or null when nothing active. */
  readonly aspectHighlight: AspectHighlight | null;
  /** Label keys selected for an aspect overlay (web hub OR replicated). */
  readonly webKeys: ReadonlySet<string>;
  readonly toggleWebKey: (key: string) => void;
  /** Keys (⊆ webKeys) rendered in `replicated` mode (per-member duplicates). */
  readonly replicatedKeys: ReadonlySet<string>;
  /** The effective overlay mode of a key, folding the active view's
   *  `style @@key { render: … }` directive over the UI pick. */
  readonly aspectModeOf: (key: string) => "off" | "web" | "replicated";
  /** Set a key's overlay mode (UI pick; persists). */
  readonly setAspectMode: (key: string, mode: "off" | "web" | "replicated") => void;
  /** Synthetic aspect nodes + member→aspect edges; empty when no keys. */
  readonly aspectOverlay: AspectOverlay;
}

const EMPTY_OVERLAY: AspectOverlay = { modules: [], edges: [], parentByNodeId: new Map() };

const COLOR_PREF_KEY = "archlang.aspectColor.v1";
const WEB_PREF_KEY = "archlang.aspectWeb.v1";
const REP_PREF_KEY = "archlang.aspectReplicated.v1";
import { hostStorage } from "../host/hostStorage.js";

function readColorPref(): string | null {
  return hostStorage.getItem(COLOR_PREF_KEY);
}
function writeColorPref(key: string | null): void {
  if (key === null) hostStorage.removeItem(COLOR_PREF_KEY);
  else hostStorage.setItem(COLOR_PREF_KEY, key);
}
function readWebPref(): readonly string[] {
  return readKeyPref(WEB_PREF_KEY);
}
function writeWebPref(keys: ReadonlySet<string>): void {
  writeKeyPref(WEB_PREF_KEY, keys);
}
function readRepPref(): readonly string[] {
  return readKeyPref(REP_PREF_KEY);
}
function writeRepPref(keys: ReadonlySet<string>): void {
  writeKeyPref(REP_PREF_KEY, keys);
}
function readKeyPref(k: string): readonly string[] {
  try {
    const parsed: unknown = JSON.parse(hostStorage.getItem(k) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function writeKeyPref(k: string, keys: ReadonlySet<string>): void {
  if (keys.size === 0) hostStorage.removeItem(k);
  else hostStorage.setItem(k, JSON.stringify([...keys]));
}

/** Persisted aspect picks — the store's `initialAspect` seed. */
export function readAspectPrefs(): {
  colorKey: string | null;
  webKeys: readonly string[];
  replicatedKeys: readonly string[];
} {
  return { colorKey: readColorPref(), webKeys: readWebPref(), replicatedKeys: readRepPref() };
}

export function useAspectOverlay(
  store: WorkspaceStore,
  model: ArchitectureModel | null,
  /** The active view's `style @@key { render: … }` modes (text directive) —
   *  wins over the UI pick where present. */
  viewRenderModes?: ReadonlyMap<string, "web" | "replicated">,
): AspectOverlayController {
  const modules = model?.modules ?? null;
  const aspect = useWorkspaceAspect(store);
  const { colorKey: aspectColorKey, legendHover, legendPinned, webKeys, replicatedKeys } = aspect;

  // Explicit picks (these setters) write the pref; store-internal drops
  // (stale key below) deliberately don't.
  const pickAspectColorKey = useCallback<Dispatch<SetStateAction<string | null>>>(
    (action) => {
      const prev = store.getState().aspect.colorKey;
      const next = typeof action === "function" ? action(prev) : action;
      writeColorPref(next);
      store.setAspectColorKey(next);
    },
    [store],
  );
  const setLegendHoverValue = useCallback<Dispatch<SetStateAction<string | null>>>(
    (action) => {
      const prev = store.getState().aspect.legendHover;
      store.setLegendHover(typeof action === "function" ? action(prev) : action);
    },
    [store],
  );
  const setLegendPinnedValue = useCallback<Dispatch<SetStateAction<string | null>>>(
    (action) => {
      const prev = store.getState().aspect.legendPinned;
      store.setLegendPinned(typeof action === "function" ? action(prev) : action);
    },
    [store],
  );

  const aspectKeys = useMemo(
    () => (modules ? aspectKeysOf(modules) : []),
    [modules],
  );
  const aspectLegendEntries = useMemo(
    () => (modules && aspectColorKey ? aspectLegend(modules, aspectColorKey) : []),
    [modules, aspectColorKey],
  );

  // Effective highlight = hover (transient) over pin (sticky).
  const aspectHighlight = useMemo<AspectHighlight | null>(() => {
    const value = legendHover ?? legendPinned;
    if (!modules || !aspectColorKey || !value) return null;
    return {
      modules: aspectMemberIds(modules, aspectColorKey, value),
      interfaces: aspectMemberInterfaces(modules, aspectColorKey, value),
    };
  }, [modules, aspectColorKey, legendHover, legendPinned]);

  // Drop stale labels.keys the model no longer has (e.g. edited away).
  // Only once a model is actually loaded — running against the empty
  // pre-load key list would wipe the restored picks before the model
  // arrives to validate them. Store-only drops: the prefs stay intact.
  useEffect(() => {
    if (!modules) return;
    const current = store.getState().aspect;
    if (current.colorKey && !aspectKeys.includes(current.colorKey)) {
      store.setAspectColorKey(null);
    }
    // Idempotent — the store no-ops when the content is unchanged.
    store.setWebKeys(
      new Set([...current.webKeys].filter((k) => aspectKeys.includes(k))),
    );
  }, [modules, aspectKeys, store, aspect]);

  const toggleWebKey = useCallback(
    (key: string) => {
      store.toggleWebKey(key);
      writeWebPref(store.getState().aspect.webKeys);
    },
    [store],
  );

  const setAspectMode = useCallback(
    (key: string, mode: "off" | "web" | "replicated") => {
      store.setAspectMode(key, mode);
      const a = store.getState().aspect;
      writeWebPref(a.webKeys);
      writeRepPref(a.replicatedKeys);
    },
    [store],
  );

  // The active view's render directive wins over the UI pick per key; the union
  // of both drives which keys draw an overlay at all.
  const effective = useMemo(() => {
    const keys = new Set<string>(webKeys);
    if (viewRenderModes) for (const k of viewRenderModes.keys()) keys.add(k);
    const replicated = new Set<string>();
    for (const k of keys) {
      const vm = viewRenderModes?.get(k);
      if (vm === "replicated" || (vm === undefined && replicatedKeys.has(k))) replicated.add(k);
    }
    return { keys, replicated };
  }, [webKeys, replicatedKeys, viewRenderModes]);

  const aspectModeOf = useCallback(
    (key: string): "off" | "web" | "replicated" =>
      !effective.keys.has(key) ? "off" : effective.replicated.has(key) ? "replicated" : "web",
    [effective],
  );

  const aspectOverlay = useMemo(
    () =>
      modules && effective.keys.size > 0
        ? buildAspectOverlay(modules, { keys: effective.keys, replicatedKeys: effective.replicated })
        : EMPTY_OVERLAY,
    [modules, effective],
  );

  return {
    aspectKeys,
    aspectColorKey,
    setAspectColorKey: pickAspectColorKey,
    aspectLegendEntries,
    legendHoverValue: legendHover,
    setLegendHoverValue,
    legendPinnedValue: legendPinned,
    setLegendPinnedValue,
    aspectHighlight,
    webKeys,
    toggleWebKey,
    replicatedKeys,
    aspectModeOf,
    setAspectMode,
    aspectOverlay,
  };
}
